/**
 * /login — email + password login (no LINE).
 *
 * Owner must first enable web login from inside LIFF
 * (ตั้งค่า → เข้าระบบผ่านเว็บ) before this works. Then this page lets
 * them sign in from any desktop browser.
 *
 * Same credential works on paypilot/jobpilot/evalpilot since all three
 * workers share the same Supabase web_identities table.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, KeyRound, LogIn, MessageCircle } from 'lucide-react';
import { apiFetch, ApiError } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { login } from '../lib/liff';
import type { AuthProfile } from '@payroll/shared';

export function WebLogin() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await apiFetch<{ jwt: string; profile: AuthProfile }>('/auth/web-login', {
        method: 'POST',
        body: { email: email.trim(), password },
        skipAuth: true,
      });
      setAuth(result.jwt, result.profile);
      // Land on the cross-app launcher first so the owner can pick which
      // product to work in. They can always come back from any app's
      // "เปลี่ยนแอป" button.
      navigate('/launcher', { replace: true });
    } catch (e2) {
      const msg = e2 instanceof ApiError ? e2.message : e2 instanceof Error ? e2.message : 'เข้าสู่ระบบไม่สำเร็จ';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen flex">
      {/* LEFT HERO (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-mangmee-hero relative items-end p-12 overflow-hidden">
        <svg viewBox="0 0 40 40" className="absolute -bottom-8 -right-8 w-72 h-72 opacity-10" aria-hidden>
          <rect x="2" y="3" width="6.5" height="34" rx="1" fill="#ffffff" />
          <path d="M12 37 V11 L21 26 L30 11 V37" fill="none" stroke="#ffffff" strokeWidth="6.4" strokeLinejoin="miter" />
        </svg>
        <div className="relative z-10">
          <div className="text-[10px] font-display font-semibold tracking-[.26em] text-gold mb-auto">HR &amp; PAYROLL SYSTEM</div>
          <h1 className="font-display text-4xl font-semibold leading-tight text-white mt-6">
            เรื่องเงินเดือน<br />ที่เคยยุ่งยาก<br />จัดการจบใน<span className="text-gold-bright">ที่เดียว</span>
          </h1>
          <p className="text-cream/80 text-sm mt-4 max-w-xs font-light">
            พนักงาน เวลาเข้างาน การลา OT และเงินเดือน ครบวงจร สำหรับ บริษัท มั่งมีทรัพย์168 จำกัด
          </p>
          <div className="w-12 h-0.5 bg-gold rounded mt-6" />
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="w-full lg:w-1/2 bg-paper-card flex items-center justify-center p-6">
        <section className="w-full max-w-sm">
          <img src="/ruaylonfa-logo.jpg" alt="ร่ำรวยล้นฟ้า 168" className="h-16 w-auto rounded-lg shadow-sm mb-8" />
          <h2 className="font-display text-2xl font-semibold text-ink">เข้าสู่ระบบ</h2>
          <p className="text-sm text-warm-gray mt-1 mb-6">ยินดีต้อนรับกลับมา — กรอกอีเมลและรหัสผ่านเพื่อเข้าใช้งาน</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">อีเมล</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="email"
                  autoComplete="email"
                  className="input pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">รหัสผ่าน</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="password"
                  autoComplete="current-password"
                  className="input pl-9"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
              </div>
            </div>

            {err && (
              <div className="rounded-lg bg-[#fbe8f1] px-3 py-2 text-sm text-[#b4564f]">{err}</div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full">
              <LogIn className="w-4 h-4" />
              {busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
            </button>

            {/* divider */}
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted my-2">
              <span className="h-px flex-1 bg-[color:rgba(13,11,10,.10)]" />หรือ<span className="h-px flex-1 bg-[color:rgba(13,11,10,.10)]" />
            </div>

            {/* LINE button — KEEP #06C755 */}
            <button
              type="button"
              onClick={() => login(window.location.origin + '/')}
              className="w-full rounded-full bg-[#06C755] px-4 py-2.5 text-sm font-semibold text-white hover:brightness-95 flex items-center justify-center gap-2 transition"
            >
              <MessageCircle className="w-4 h-4" /> เข้าสู่ระบบด้วย LINE
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
